/* Color noise synthesis via Web Audio API.
 *
 * Generates 7 colors of noise (white, pink, brown, green, blue, violet, grey)
 * plus an optional amplitude modulator that produces micro-pulses in the
 * delta-wave band (0.5–4 Hz) — used by the "Sleep" preset to nudge deep-sleep
 * entrainment.
 *
 * All sources are ScriptProcessorNode-based so they stream infinite unique
 * samples with no loop artefacts.
 */

const COLORS = ['white', 'pink', 'brown', 'green', 'blue', 'violet', 'grey'];

const COLOR_META = {
    white:  { label: 'White',  hue: '#e8e8e8', desc: 'Even energy across the spectrum. Blankets outside noise for studying.' },
    pink:   { label: 'Pink',   hue: '#f2c4c4', desc: 'Softer top-end, warm and steady. The classic focus/memory noise.' },
    brown:  { label: 'Brown',  hue: '#a68a70', desc: 'Deep and rumbling, like distant surf. Calming for relaxation.' },
    green:  { label: 'Green',  hue: '#a8c5b0', desc: 'A gentle mid-band emphasis over pink. Nature-like and grounding.' },
    blue:   { label: 'Blue',   hue: '#a4c1e2', desc: 'Brighter, airier. Some report it aids high-alert focus.' },
    violet: { label: 'Violet', hue: '#c8a0d8', desc: 'Highest energy in the top end. Used therapeutically for tinnitus.' },
    grey:   { label: 'Grey',   hue: '#c8c1b7', desc: 'A-weighted to match human hearing. Feels perfectly uniform.' },
};

/**
 * Build a stream-generating node for a given color. Returns a
 * `ScriptProcessorNode` that continuously synthesises stereo noise.
 * Deprecated in the standard but still ubiquitous; AudioWorklet migration
 * can happen later without changing this module's public interface.
 */
function makeNoiseNode(ctx, color) {
    const bufferSize = 4096;
    const node = ctx.createScriptProcessor(bufferSize, 0, 2);

    // Per-color state closed over the process callback.
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0; // pink coeffs
    let brown = 0;                                                // brown random walk
    let lastWhite = 0;                                            // blue/violet differentiator memory
    let lastLastWhite = 0;                                        // violet second-difference memory
    // Bandpass state for green (simple biquad approx via one-pole HP+LP)
    let greenPinkLp = 0, greenPinkHp = 0;

    // Precomputed peaking coefficient for grey (A-weighted approx around 3kHz).
    // We shape white noise with a small parametric emphasis to approximate the
    // A-curve without the CPU cost of a full IIR chain.
    const greyMidGain = 1.6;

    const processors = {
        white: (out) => (out[0] = Math.random() * 2 - 1),

        // Paul Kellet "refined" pink filter — cheap, colour-correct within ±0.5dB.
        pink: (out) => {
            const white = Math.random() * 2 - 1;
            b0 = 0.99886 * b0 + white * 0.0555179;
            b1 = 0.99332 * b1 + white * 0.0750759;
            b2 = 0.96900 * b2 + white * 0.1538520;
            b3 = 0.86650 * b3 + white * 0.3104856;
            b4 = 0.55000 * b4 + white * 0.5329522;
            b5 = -0.7616 * b5 - white * 0.0168980;
            out[0] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
            b6 = white * 0.115926;
        },

        brown: (out) => {
            const white = Math.random() * 2 - 1;
            brown = (brown + 0.02 * white) / 1.02;
            out[0] = brown * 3.5;
        },

        // Blue = high-pass of white (differentiator, +3dB/oct).
        blue: (out) => {
            const white = Math.random() * 2 - 1;
            out[0] = (white - lastWhite) * 0.5;
            lastWhite = white;
        },

        // Violet = second difference of white (+6dB/oct).
        violet: (out) => {
            const white = Math.random() * 2 - 1;
            out[0] = (white - 2 * lastWhite + lastLastWhite) * 0.25;
            lastLastWhite = lastWhite;
            lastWhite = white;
        },

        // Grey = white with a mid-band emphasis approximating A-weighting.
        // We synthesise white, then push it through a tiny bandpass and
        // sum it back with a subtle gain bump.
        grey: (out) => {
            const white = Math.random() * 2 - 1;
            greenPinkLp += 0.15 * (white - greenPinkLp);
            greenPinkHp = white - greenPinkLp;
            out[0] = (white * 0.6 + greenPinkHp * greyMidGain * 0.4);
        },

        // Green = pink noise with a soft bandpass emphasis around 400–600Hz
        // for a nature-like, mid-focused character.
        green: (out) => {
            const white = Math.random() * 2 - 1;
            b0 = 0.99886 * b0 + white * 0.0555179;
            b1 = 0.99332 * b1 + white * 0.0750759;
            b2 = 0.96900 * b2 + white * 0.1538520;
            b3 = 0.86650 * b3 + white * 0.3104856;
            b4 = 0.55000 * b4 + white * 0.5329522;
            b5 = -0.7616 * b5 - white * 0.0168980;
            const pink = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
            b6 = white * 0.115926;
            // one-pole bandpass on pink
            greenPinkLp += 0.08 * (pink - greenPinkLp);
            greenPinkHp = pink - greenPinkLp;
            out[0] = greenPinkLp * 1.8 + greenPinkHp * 0.4;
        },
    };

    const step = processors[color] || processors.white;
    const out = new Float32Array(1);

    node.onaudioprocess = (ev) => {
        const left = ev.outputBuffer.getChannelData(0);
        const right = ev.outputBuffer.getChannelData(1);
        for (let i = 0; i < bufferSize; i++) {
            step(out);
            // Small L/R decorrelation on the same colour tone: right channel gets
            // an independent sample so the noise has natural stereo width.
            left[i] = out[0];
            step(out);
            right[i] = out[0];
        }
    };

    return node;
}


/**
 * Public player. Play any color of noise with an optional sleep-mode
 * amplitude modulator, at a controllable volume. All state is contained.
 */
export class ColorNoisePlayer {
    constructor() {
        this.ctx = null;
        this.gain = null;
        this.lfo = null;      // sleep-mode modulator
        this.lfoGain = null;
        this.noiseNode = null;
        this.playing = false;
        this.volume = 0.4;
    }

    _ensureContext() {
        if (this.ctx) return;
        const AC = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AC();
        this.gain = this.ctx.createGain();
        this.gain.gain.value = this.volume;
        this.gain.connect(this.ctx.destination);
    }

    async play(color, { sleep = false, sleepRateHz = 1.3, sleepDepth = 0.65 } = {}) {
        this._ensureContext();
        if (this.ctx.state === 'suspended') await this.ctx.resume();
        this.stop({ keepContext: true });

        this.noiseNode = makeNoiseNode(this.ctx, color);

        if (sleep) {
            // Micro-pulse the amplitude at 0.5–4 Hz to loosely coincide with the
            // brain's delta wave band. `sleepDepth` controls how deep the dip is
            // between pulses (0 = no modulation, 1 = full silence between pulses).
            this.lfo = this.ctx.createOscillator();
            this.lfo.frequency.value = sleepRateHz;
            this.lfoGain = this.ctx.createGain();
            this.lfoGain.gain.value = sleepDepth / 2;

            const modulatedGain = this.ctx.createGain();
            modulatedGain.gain.value = 1 - sleepDepth / 2;

            this.lfo.connect(this.lfoGain);
            this.lfoGain.connect(modulatedGain.gain);
            this.noiseNode.connect(modulatedGain);
            modulatedGain.connect(this.gain);
            this.lfo.start();
        } else {
            this.noiseNode.connect(this.gain);
        }

        this.playing = true;
    }

    stop({ keepContext = false } = {}) {
        try { this.noiseNode?.disconnect(); } catch { /* no-op */ }
        try { this.lfo?.stop(); this.lfo?.disconnect(); } catch { /* no-op */ }
        try { this.lfoGain?.disconnect(); } catch { /* no-op */ }
        this.noiseNode = null;
        this.lfo = null;
        this.lfoGain = null;
        this.playing = false;
        if (!keepContext && this.ctx) {
            try { this.ctx.close(); } catch { /* no-op */ }
            this.ctx = null;
            this.gain = null;
        }
    }

    setVolume(v) {
        this.volume = Math.max(0, Math.min(1, v));
        if (this.gain) {
            const t = this.ctx?.currentTime ?? 0;
            // Smooth ramp so volume changes don't click.
            this.gain.gain.cancelScheduledValues(t);
            this.gain.gain.linearRampToValueAtTime(this.volume, t + 0.08);
        }
    }
}

export { COLORS, COLOR_META };
